import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MobXProviderContext, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { BringgInput, Tabs, TabPane } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { type RootStore } from '@bringg-frontend/global-stores';
import notification from '@bringg/react-components/dist/components/notification/notification';

import { DEFAULT_LANGUAGE_CODE, useColumnLabelsLanguages } from './utils/language-service';
import { getColumnsByTable } from './column-label-definitions';
import {
	type ColumnLabelOverrides,
	type ColumnTableId,
	ORDER_MANAGER_TABLE_ID,
	ROUTE_MONITOR_TABLE_ID,
	updateColumnLabelOverride
} from './utils/column-label-overrides';
import ColumnsLabelsHeader from './columns-labels-header/columns-labels-header';
import ColumnsLabelsTable from './columns-labels-table/columns-labels-table';
import LanguagesControl from './languages-control/languages-control';

import styles from './column-labels-manager.module.scss';

const useColumnLabelsStores = () => React.useContext(MobXProviderContext) as RootStore['data'] & RootStore['views'];

const ColumnLabelsManager: React.FC = observer(() => {
	const { t } = useTranslation();
	const { merchantConfigurationsStore, usersStore } = useColumnLabelsStores();
	const defaultLanguageCode =
		usersStore.currentUserLanguage || merchantConfigurationsStore.defaultLanguageCode || DEFAULT_LANGUAGE_CODE;
	const [activeTableId, setActiveTableId] = useState<ColumnTableId>(ROUTE_MONITOR_TABLE_ID);
	const [overrides, setOverrides] = useState<ColumnLabelOverrides>(
		merchantConfigurationsStore.columnLabelOverrides || {}
	);
	const [search, setSearch] = useState<string>('');
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const {
		activeLanguageCode,
		activeLanguages,
		availableLanguagesToAdd,
		activeLanguageName,
		setActiveLanguageCode,
		onAddLanguage,
		onRemoveLanguage
	} = useColumnLabelsLanguages({
		storedOverrides: merchantConfigurationsStore.columnLabelOverrides || {},
		setOverrides,
		defaultLanguageCode
	});

	useEffect(() => {
		setOverrides(merchantConfigurationsStore.columnLabelOverrides || {});
	}, [merchantConfigurationsStore.columnLabelOverrides]);

	const isDirty = useMemo(() => {
		const initialOverrides = merchantConfigurationsStore.columnLabelOverrides || {};
		return JSON.stringify(initialOverrides) !== JSON.stringify(overrides);
	}, [merchantConfigurationsStore.columnLabelOverrides, overrides]);

	const onLabelChange = useCallback(
		(columnId: string, value: string) => {
			setOverrides(prev =>
				updateColumnLabelOverride(prev || {}, activeTableId, activeLanguageCode, columnId, value)
			);
		},
		[activeTableId, activeLanguageCode, setOverrides]
	);

	const onSave = useCallback(async () => {
		setIsSaving(true);
		try {
			await merchantConfigurationsStore.updateColumnLabelOverrides(overrides);
			notification.success(t('WEB_USER_INTERFACE.COLUMN_LABELS.SAVED'));
		} catch {
			notification.error(t('WEB_USER_INTERFACE.COLUMN_LABELS.SAVE_FAILED'));
		} finally {
			setIsSaving(false);
		}
	}, [merchantConfigurationsStore, overrides, t]);

	const columns = getColumnsByTable(activeTableId);
	const filteredColumns = useMemo(() => {
		if (!search.trim()) return columns;
		const query = search.trim().toLowerCase();
		return columns.filter(col => col.label.toLowerCase().includes(query));
	}, [columns, search]);

	return (
		<div className={styles.columnLabels} data-test-id="column-labels">
			<ColumnsLabelsHeader isDirty={isDirty} isSaving={isSaving} onSave={onSave} />

			<Tabs
				className={styles.tableTabs}
				activeKey={activeTableId}
				onChange={key => setActiveTableId(key as ColumnTableId)}
				data-test-id="column-labels-table-tabs"
			>
				<TabPane tab={t('WEB_USER_INTERFACE.COLUMN_LABELS.ROUTE_MONITOR')} key={ROUTE_MONITOR_TABLE_ID} />
				<TabPane tab={t('WEB_USER_INTERFACE.COLUMN_LABELS.ORDER_MANAGER')} key={ORDER_MANAGER_TABLE_ID} />
			</Tabs>

			<div className={styles.controls}>
				<LanguagesControl
					activeLanguageCode={activeLanguageCode}
					defaultLanguageCode={defaultLanguageCode}
					activeLanguages={activeLanguages}
					availableLanguagesToAdd={availableLanguagesToAdd}
					setActiveLanguageCode={setActiveLanguageCode}
					onAddLanguage={onAddLanguage}
					onRemoveLanguage={onRemoveLanguage}
				/>
				<div className={styles.columnLabelsSearch}>
					<BringgInput
						prefix={<BringgIcon iconName={BringgFontIcons.Search} />}
						placeholder={t('WEB_USER_INTERFACE.COLUMN_LABELS.SEARCH_PLACEHOLDER')}
						value={search}
						onChange={e => setSearch(e.target.value)}
						data-test-id="column-labels-search"
					/>
				</div>
			</div>

			<ColumnsLabelsTable
				filteredColumns={filteredColumns}
				overrides={overrides}
				activeTableId={activeTableId}
				activeLanguageCode={activeLanguageCode}
				activeLanguageName={activeLanguageName}
				onLabelChange={onLabelChange}
			/>
		</div>
	);
});

export default ColumnLabelsManager;
